import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import "firebase/auth";

import { saveUserLocally } from "../helpers/saveUserLocally";
import {
    saveUserEmailForOffline,
    checkOfflineUserEmail,
    getAvailableOfflineEmails
} from "../helpers/offlineUserManager";

import SubscriptionModal from "./SubscriptionModal";

import {
    fetchUser,
    signIn,
    resetPassword
} from "../redux/actions/user";

import {
    authenticateOffline,
    syncFirestoreUsersToPouch
} from "../redux/actions/local";

import { testNetworkConnectivity } from "../helpers/offlineApi";
import { runBackgroundOfflineSync } from "../helpers/offlineSync";
import { setMessage } from "../redux/actions/status";

import {
    Grid,
    Row,
    Col,
    FormGroup,
    ControlLabel,
    FormControl,
    Alert,
    Modal,
    Button
} from "react-bootstrap";

import bgImage from "assets/img/pharmacy.jpg";

const API_BASE_URL =
    "https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo";

class Login extends Component {
    constructor(props) {
        super(props);

        this._isMounted = true;

        this.state = {
            cardHidden: true,

            email: "",
            password: "",
            resetEmail: "",

            showPassword: false,

            showResetModal: false,
            showPrivacyModal: false,
            showSubscriptionModal: false,

            isOnline: true,
            checkingConnectivity: true,

            loginInProgress: false,
            offlineLoginInProgress: false,

            redirecting: false,

            availableUsers: [],
            offlineEmails: [],

            subscriptionStatus: null,
            payLink: null
        };
    }

    /* =========================================================
       CONNECTION
    ========================================================= */

    async checkConnectivityWithRetry() {
        let isOnline = false;
        let retries = 0;

        const maxRetries = 3;

        while (retries < maxRetries && !isOnline) {
            try {
                isOnline = await testNetworkConnectivity();

                if (!isOnline) {
                    await new Promise(resolve =>
                        setTimeout(resolve, 1000)
                    );
                }
            } catch (error) {
                // Ignorer l'erreur et continuer
            }

            retries++;
        }

        return isOnline;
    }

    async componentDidMount() {
        window.addEventListener(
            "online",
            this.handleConnectionChange
        );

        window.addEventListener(
            "offline",
            this.handleConnectionChange
        );

        window.addEventListener(
            "auth-network-failed",
            this.handleAuthNetworkFailed
        );

        window.addEventListener(
            "force-offline-mode",
            this.handleForceOfflineMode
        );

        window.addEventListener(
            "force-offline-due-to-cors",
            this.handleCORSOfflineSwitch
        );

        window.addEventListener(
            "auto-offline-login",
            this.handleAutoOfflineLogin
        );

        this.cardTimeout = setTimeout(() => {
            if (this._isMounted) {
                this.setState({
                    cardHidden: false
                });
            }
        }, 400);

        await this.checkConnectivity();

        if (!this._isMounted) return;

        await this.props.fetchUser();

        if (!this.state.isOnline) {
            const cachedUser =
                localStorage.getItem("lastLoggedUser");

            if (cachedUser) {
                try {
                    const userData = JSON.parse(cachedUser);

                    if (userData.email) {
                        await this.props.authenticateOffline(
                            userData.email
                        );
                    }
                } catch (error) {
                    // Ignorer
                }
            }

            await this.loadAvailableUsers();
            await this.loadOfflineEmails();
        }
    }

    componentDidUpdate(prevProps) {
        const {
            isOnline,
            redirecting,
            showSubscriptionModal
        } = this.state;

        const {
            user,
            authDone,
            offlineUser,
            offlineAuthDone,
            loading,
            authenticating
        } = this.props;

        const shouldRedirect =
            !loading &&
            !authenticating &&
            authDone &&
            (
                (isOnline && user) ||
                (!isOnline && offlineUser && offlineAuthDone)
            ) &&
            !redirecting &&
            !showSubscriptionModal;

        if (shouldRedirect) {
            this.setState(
                {
                    redirecting: true
                },
                () => {
                    try {
                        if (
                            !this.props.history ||
                            !this.props.history.push
                        ) {
                            throw new Error(
                                "History non disponible"
                            );
                        }

                        this.props.history.push("/app");

                        setTimeout(() => {
                            if (
                                this._isMounted &&
                                window.location.pathname === "/"
                            ) {
                                window.location.href = "/app";
                            }
                        }, 300);
                    } catch (error) {
                        window.location.href = "/app";
                    }
                }
            );
        }
    }

    componentWillUnmount() {
        this._isMounted = false;

        window.removeEventListener(
            "online",
            this.handleConnectionChange
        );

        window.removeEventListener(
            "offline",
            this.handleConnectionChange
        );

        window.removeEventListener(
            "auth-network-failed",
            this.handleAuthNetworkFailed
        );

        window.removeEventListener(
            "force-offline-mode",
            this.handleForceOfflineMode
        );

        window.removeEventListener(
            "force-offline-due-to-cors",
            this.handleCORSOfflineSwitch
        );

        window.removeEventListener(
            "auto-offline-login",
            this.handleAutoOfflineLogin
        );

        if (this.cardTimeout) {
            clearTimeout(this.cardTimeout);
        }
    }

    /* =========================================================
       OFFLINE EVENTS
    ========================================================= */

    handleForceOfflineMode = async event => {
        const {
            email,
            hasOfflineData
        } = event.detail || {};

        if (hasOfflineData && email) {
            this.setState(
                {
                    isOnline: false,
                    email,
                    loginInProgress: false,
                    checkingConnectivity: false
                },
                async () => {
                    await this.loadAvailableUsers();
                    await this.loadOfflineEmails();

                    this.props.setMessage({
                        message:
                            "🔄 Mode hors ligne activé. Connexion automatique en cours...",
                        status: "info"
                    });

                    setTimeout(async () => {
                        try {
                            await this.handleOfflineLogin(email);
                        } catch (error) {
                            this.props.setMessage({
                                message:
                                    "Mode hors ligne activé. Entrez votre email pour vous connecter.",
                                status: "warning"
                            });
                        }
                    }, 1500);
                }
            );
        }
    };

    handleCORSOfflineSwitch = () => {
        this.setState({
            isOnline: false,
            loginInProgress: false,
            checkingConnectivity: false
        });

        this.props.setMessage({
            message:
                "🚨 Erreurs CORS détectées. Basculement automatique vers le mode hors ligne...",
            status: "warning"
        });
    };

    handleAutoOfflineLogin = async event => {
        const {
            email
        } = event.detail || {};

        if (email) {
            this.setState(
                {
                    isOnline: false,
                    email,
                    loginInProgress: false
                },
                async () => {
                    try {
                        await this.handleOfflineLogin(email);
                    } catch (error) {
                        this.props.setMessage({
                            message:
                                `Impossible de se connecter automatiquement en mode hors ligne avec ${email}.`,
                            status: "warning"
                        });
                    }
                }
            );
        }
    };

    handleAuthNetworkFailed = event => {
        const {
            email,
            shouldSwitchToOffline
        } = event.detail || {};

        if (shouldSwitchToOffline && email) {
            this.setState(
                {
                    isOnline: false,
                    email,
                    loginInProgress: false
                },
                async () => {
                    setTimeout(async () => {
                        try {
                            await this.handleOfflineLogin(email);
                        } catch (error) {
                            this.props.setMessage({
                                message:
                                    "Impossible de se connecter en mode hors ligne avec cet email.",
                                status: "danger"
                            });
                        }
                    }, 1000);
                }
            );
        }
    };

    /* =========================================================
       CONNECTIVITY
    ========================================================= */

    handleConnectionChange = async () => {
        const isOnline =
            await this.checkConnectivityWithRetry();

        if (!this._isMounted) return;

        this.setState({
            isOnline
        });

        if (isOnline) {
            this.props.setMessage({
                message:
                    "Connexion internet rétablie",
                status: "success"
            });
        } else {
            this.props.setMessage({
                message:
                    "Mode hors ligne activé",
                status: "warning"
            });

            await this.loadAvailableUsers();
            await this.loadOfflineEmails();
        }
    };

    async checkConnectivity() {
        if (!this._isMounted) return;

        this.setState({
            checkingConnectivity: true
        });

        try {
            const isOnline =
                await this.checkConnectivityWithRetry();

            if (!this._isMounted) return;

            this.setState({
                isOnline,
                checkingConnectivity: false
            });

            if (!isOnline) {
                await this.loadAvailableUsers();
                await this.loadOfflineEmails();

                const offlineEmails =
                    await getAvailableOfflineEmails();

                if (offlineEmails.length > 0) {
                    this.props.setMessage({
                        message:
                            `📱 Mode hors ligne activé. ${offlineEmails.length} utilisateur(s) disponible(s).`,
                        status: "info"
                    });
                } else {
                    this.props.setMessage({
                        message:
                            "📱 Mode hors ligne activé. Aucun utilisateur disponible.",
                        status: "warning"
                    });
                }
            }
        } catch (error) {
            if (!this._isMounted) return;

            this.setState({
                isOnline: false,
                checkingConnectivity: false
            });

            await this.loadAvailableUsers();
            await this.loadOfflineEmails();
        }
    }

    handleRetryConnection = async () => {
        this.setState({
            checkingConnectivity: true
        });

        try {
            const {
                forceConnectivityCheck
            } = await import("../helpers/offlineApi");

            const isOnline =
                await forceConnectivityCheck(5000);

            this.setState({
                isOnline,
                checkingConnectivity: false
            });

            if (isOnline) {
                this.props.setMessage({
                    message:
                        "✅ Connexion internet détectée !",
                    status: "success"
                });

                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                this.props.setMessage({
                    message:
                        "❌ Aucune connexion internet détectée",
                    status: "warning"
                });
            }
        } catch (error) {
            this.setState({
                checkingConnectivity: false
            });

            this.props.setMessage({
                message:
                    "Erreur lors de la vérification de connexion",
                status: "danger"
            });
        }
    };

    /* =========================================================
       LOCAL DATA
    ========================================================= */

    async loadAvailableUsers() {
        try {
            const {
                userDb
            } = await import("../config/pouch");

            const allUsers =
                await userDb.allDocs({
                    include_docs: true
                });

            const users = allUsers.rows
                .map(row => ({
                    id: row.id,
                    email: row.doc.email,
                    name:
                        row.doc.name ||
                        row.doc.displayName ||
                        "Utilisateur",
                    doc: row.doc
                }))
                .filter(user => user.email);

            if (this._isMounted) {
                this.setState({
                    availableUsers: users
                });
            }
        } catch (error) {
            if (this._isMounted) {
                this.setState({
                    availableUsers: []
                });
            }
        }
    }

    loadOfflineEmails = async () => {
        try {
            const offlineEmails =
                await getAvailableOfflineEmails();

            if (this._isMounted) {
                this.setState({
                    offlineEmails
                });
            }
        } catch (error) {
            // Ignorer
        }
    };

    /* =========================================================
       INPUT
    ========================================================= */

    handleInputChange = e => {
        const {
            name,
            value
        } = e.target;

        this.setState({
            [name]: value
        });
    };

    togglePassword = () => {
        this.setState(prevState => ({
            showPassword: !prevState.showPassword
        }));
    };

    /* =========================================================
       LOGIN
    ========================================================= */

    handleSubmit = async e => {
        e.preventDefault();

        const {
            isOnline,
            email,
            password
        } = this.state;

        if (isOnline) {
            await this.handleOnlineLogin(
                email,
                password
            );
        } else {
            await this.handleOfflineLogin(
                email,
                password
            );
        }
    };

    handleOnlineLogin = async (
        email,
        password
    ) => {
        if (!email || !password) {
            return this.props.setMessage({
                message:
                    "Email et mot de passe requis.",
                status: "danger"
            });
        }

        this.setState({
            loginInProgress: true
        });

        this.props.setMessage(null);

        try {
            const result =
                await this.props.signIn(
                    email,
                    password
                );

            const user = result?.user;

            if (!user) {
                throw new Error(
                    "Utilisateur non trouvé."
                );
            }

            const token =
                await user.getIdToken();

            const res = await fetch(
                `${API_BASE_URL}/check-subscription/${encodeURIComponent(
                    user.email
                )}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type":
                            "application/json",
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            if (!res.ok) {
                throw new Error(
                    "Erreur lors de la vérification de l'abonnement."
                );
            }

            const subscriptionData =
                await res.json();

            const valid =
                subscriptionData.subscribed;

            if (!valid) {
                this.setState({
                    showSubscriptionModal: true,

                    subscriptionStatus: {
                        status:
                            subscriptionData.expiresAt
                                ? "expired"
                                : "none",

                        endDate:
                        subscriptionData.expiresAt,

                        planName:
                        subscriptionData.plan
                    },

                    payLink: null
                });

                return;
            }

            await saveUserLocally(
                user,
                password
            );

            await this.props
                .syncFirestoreUsersToPouch();

            await saveUserEmailForOffline(
                user.email,
                password,
                {
                    uid: user.uid,
                    displayName:
                    user.displayName,
                    photoURL:
                    user.photoURL
                }
            );

            this.setState({
                redirecting: true
            });

            this.props.history.push("/app");
        } catch (error) {
            const isNetworkError =
                error.message &&
                (
                    error.message.includes(
                        "network"
                    ) ||
                    error.message.includes(
                        "réseau"
                    ) ||
                    error.message.includes(
                        "auth/network-request-failed"
                    ) ||
                    error.code ===
                    "auth/network-request-failed"
                );

            if (isNetworkError) {
                try {
                    const offlineUser =
                        await checkOfflineUserEmail(
                            email
                        );

                    if (offlineUser) {
                        this.props.setMessage({
                            message:
                                "⚠️ Problème réseau. Basculement vers le mode hors ligne...",
                            status: "warning"
                        });

                        setTimeout(() => {
                            this.setState(
                                {
                                    isOnline: false,
                                    loginInProgress: false
                                },
                                async () => {
                                    await this.handleOfflineLogin(
                                        email,
                                        password
                                    );
                                }
                            );
                        }, 1500);

                        return;
                    }
                } catch (offlineError) {
                    // Continuer avec l'erreur originale
                }
            }

            this.props.setMessage({
                message:
                    error.message ||
                    "Échec de la connexion. Vérifiez vos identifiants.",
                status: "danger"
            });
        } finally {
            if (this._isMounted) {
                this.setState({
                    loginInProgress: false
                });
            }
        }
    };

    handleOfflineLogin = async (
        email,
        password
    ) => {
        if (!email) {
            return this.props.setMessage({
                message:
                    "Email requis pour la connexion hors ligne.",
                status: "danger"
            });
        }

        const emailRegex =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(email)) {
            return this.props.setMessage({
                message:
                    "Format d'email invalide.",
                status: "danger"
            });
        }

        if (!password) {
            return this.props.setMessage({
                message:
                    "Veuillez saisir votre mot de passe.",
                status: "warning"
            });
        }

        this.setState({
            offlineLoginInProgress: true
        });

        this.props.setMessage(null);

        try {
            const offlineUser =
                await checkOfflineUserEmail(
                    email
                );

            if (!offlineUser) {
                if (
                    this.state.availableUsers.length ===
                    0
                ) {
                    throw new Error(
                        "Utilisateur non trouvé. Connectez-vous d'abord en ligne."
                    );
                }

                const userExists =
                    this.state.availableUsers.some(
                        user =>
                            user.email.toLowerCase() ===
                            email.toLowerCase()
                    );

                if (!userExists) {
                    throw new Error(
                        `Utilisateur avec l'email "${email}" non trouvé hors ligne.`
                    );
                }
            }

            await this.props.authenticateOffline(
                email,
                password
            );

            this.props.setMessage({
                message:
                    `✅ Connexion hors ligne réussie pour ${email}`,
                status: "success",
                timestamp: Date.now()
            });

            this.setState({
                redirecting: true
            });

            setTimeout(() => {
                try {
                    if (
                        this.props.history &&
                        this.props.history.push
                    ) {
                        this.props.history.push(
                            "/app"
                        );
                    } else {
                        window.location.href =
                            "/app";
                    }
                } catch (error) {
                    window.location.href =
                        "/app";
                }
            }, 700);
        } catch (error) {
            this.props.setMessage({
                message:
                    error.message ||
                    "Échec de la connexion hors ligne.",
                status: "danger"
            });
        } finally {
            if (this._isMounted) {
                this.setState({
                    offlineLoginInProgress: false
                });
            }
        }
    };

    /* =========================================================
       OTHER ACTIONS
    ========================================================= */

    handleSwitchToOffline = async () => {
        const {
            email
        } = this.state;

        if (!email) {
            return this.props.setMessage({
                message:
                    "Veuillez entrer votre email.",
                status: "warning"
            });
        }

        try {
            const offlineUser =
                await checkOfflineUserEmail(
                    email
                );

            if (!offlineUser) {
                return this.props.setMessage({
                    message:
                        "Aucune donnée hors ligne trouvée pour cet email.",
                    status: "warning"
                });
            }

            this.setState(
                {
                    isOnline: false
                },
                async () => {
                    this.props.setMessage({
                        message:
                            "🔄 Basculement vers le mode hors ligne...",
                        status: "info"
                    });

                    await this.loadAvailableUsers();
                    await this.loadOfflineEmails();
                }
            );
        } catch (error) {
            this.props.setMessage({
                message:
                    "Erreur lors de la vérification des données hors ligne.",
                status: "danger"
            });
        }
    };

    handleResetPassword = () => {
        const {
            resetEmail
        } = this.state;

        if (!resetEmail) {
            return this.props.setMessage({
                message:
                    "Veuillez entrer une adresse email valide.",
                status: "danger"
            });
        }

        this.setState({
            showResetModal: false
        });

        this.props.resetPassword(
            resetEmail
        );
    };

    handleSubscriptionModalClose = () => {
        this.setState({
            showSubscriptionModal: false
        });
    };

    /* =========================================================
       CONNECTIVITY SCREEN
    ========================================================= */

    renderConnectivityCheck() {
        return (
            <div style={styles.page}>
                <div
                    style={{
                        ...styles.loadingCard,
                        backgroundImage:
                            `url(${bgImage})`
                    }}
                >
                    <div style={styles.loadingOverlay}>
                        <div style={styles.spinnerCircle}>
                            <i className="fa fa-spinner fa-spin" />
                        </div>

                        <h3>
                            Vérification de la connexion
                        </h3>

                        <p>
                            Vérification de votre connexion
                            internet...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    /* =========================================================
       LOGIN FORM
    ========================================================= */

    renderLoginForm() {
        const {
            isOnline,
            email,
            password,
            loginInProgress,
            offlineLoginInProgress,
            redirecting,
            showPassword,
            offlineEmails
        } = this.state;

        const {
            message
        } = this.props;

        const loading =
            isOnline
                ? loginInProgress
                : offlineLoginInProgress;

        if (redirecting) {
            return (
                <div style={styles.successContainer}>
                    <div style={styles.successIcon}>
                        <i className="fa fa-check" />
                    </div>

                    <h2>
                        Connexion réussie !
                    </h2>

                    <p>
                        Redirection vers LINFOMED...
                    </p>

                    <i
                        className="fa fa-spinner fa-spin"
                        style={{
                            fontSize: 22,
                            color: "#1769aa"
                        }}
                    />
                </div>
            );
        }

        return (
            <Grid fluid style={styles.loginGrid}>
                <Row>
                    <Col xs={12}>
            <div className="panel panel-default" style={styles.loginContainer}>

                {/* IMAGE GAUCHE */}
                <div
                    className="login-image-panel"
                    style={{
                        ...styles.imagePanel,
                        backgroundImage:
                            `url(${bgImage})`
                    }}
                >
                    <div style={styles.imageOverlay}>

                        <div style={styles.logoCircle}>
                            <i className="fa fa-medkit" />
                        </div>

                        <div>
                            <h1 style={styles.imageTitle}>
                                APURMED
                            </h1>

                            <p style={styles.imageSubtitle}>
                                Votre solution numérique
                                pour la gestion pharmaceutique
                            </p>
                        </div>

                        <div style={styles.imageBottom}>
                            <div>
                                <i className="fa fa-shield" />
                                <span>
                                    Sécurisé
                                </span>
                            </div>

                            <div>
                                <i className="fa fa-database" />
                                <span>
                                    Données protégées
                                </span>
                            </div>

                            <div>
                                <i className="fa fa-wifi" />
                                <span>
                                    Mode hors ligne
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* FORMULAIRE DROIT */}
                <div style={styles.formPanel}>

                    <div style={styles.formInner}>

                        <div style={styles.mobileLogo}>
                            <div style={styles.mobileLogoIcon}>
                                <i className="fa fa-medkit" />
                            </div>

                            <strong>
                                LINFOMED
                            </strong>
                        </div>

                        <div style={styles.formHeader}>

                            {/*<div*/}
                            {/*    style={{*/}
                            {/*        ...styles.statusBadge,*/}
                            {/*        backgroundColor:*/}
                            {/*            isOnline*/}
                            {/*                ? "#e8f7ee"*/}
                            {/*                : "#fff4df",*/}
                            {/*        color:*/}
                            {/*            isOnline*/}
                            {/*                ? "#198754"*/}
                            {/*                : "#b77900"*/}
                            {/*    }}*/}
                            {/*>*/}
                            {/*    <span*/}
                            {/*        style={{*/}
                            {/*            ...styles.statusDot,*/}
                            {/*            backgroundColor:*/}
                            {/*                isOnline*/}
                            {/*                    ? "#22c55e"*/}
                            {/*                    : "#f59e0b"*/}
                            {/*        }}*/}
                            {/*    />*/}

                            {/*    {isOnline*/}
                            {/*        ? "En ligne"*/}
                            {/*        : "Hors ligne"}*/}
                            {/*</div>*/}

                            <h2 style={styles.formTitle}>
                                Bienvenue
                            </h2>

                            <p style={styles.formSubtitle}>
                                Connectez-vous à votre compte
                            </p>
                        </div>

                        {message && (
                            <Alert
                                bsStyle={
                                    message.status
                                }
                                style={{
                                    borderRadius: 10,
                                    fontSize: 13,
                                    marginBottom: 20
                                }}
                            >
                                <i
                                    className={`fa fa-${
                                        message.status ===
                                        "success"
                                            ? "check"
                                            : message.status ===
                                            "danger"
                                                ? "times"
                                                : "info"
                                    }`}
                                    style={{
                                        marginRight: 8
                                    }}
                                />

                                <span
                                    dangerouslySetInnerHTML={{
                                        __html:
                                        message.message
                                    }}
                                />
                            </Alert>
                        )}

                        {/* MODE */}
                        <div
                            style={{
                                ...styles.modeBox,
                                backgroundColor:
                                    isOnline
                                        ? "#f0f7ff"
                                        : "#fff8e8"
                            }}
                        >
                            <div style={styles.modeIcon}>
                                <i
                                    className={`fa fa-${
                                        isOnline
                                            ? "shield"
                                            : "exclamation-triangle"
                                    }`}
                                />
                            </div>

                            <div>
                                <strong>
                                    Mode{" "}
                                    {isOnline
                                        ? "en ligne"
                                        : "hors ligne"}
                                </strong>

                                <div>
                                    {isOnline
                                        ? "Connexion sécurisée avec votre compte."
                                        : "Accès aux données synchronisées."}
                                </div>
                            </div>
                        </div>

                        <form
                            onSubmit={
                                this.handleSubmit
                            }
                        >
                            {/* EMAIL */}
                            <FormGroup>
                                <ControlLabel
                                    style={
                                        styles.label
                                    }
                                >
                                    Adresse email
                                </ControlLabel>

                                <div
                                    style={
                                        styles.inputWrapper
                                    }
                                >
                                    <i
                                        className="fa fa-envelope"
                                        style={
                                            styles.inputIcon
                                        }
                                    />

                                    <FormControl
                                        type="email"
                                        name="email"
                                        value={email}
                                        onChange={
                                            this.handleInputChange
                                        }
                                        placeholder="exemple@email.com"
                                        required
                                        style={
                                            styles.input
                                        }
                                    />
                                </div>
                            </FormGroup>

                            {/* PASSWORD */}
                            <FormGroup>
                                <ControlLabel
                                    style={
                                        styles.label
                                    }
                                >
                                    Mot de passe
                                </ControlLabel>

                                <div
                                    style={
                                        styles.inputWrapper
                                    }
                                >
                                    <i
                                        className="fa fa-lock"
                                        style={
                                            styles.inputIcon
                                        }
                                    />

                                    <FormControl
                                        type={
                                            showPassword
                                                ? "text"
                                                : "password"
                                        }
                                        name="password"
                                        value={password}
                                        onChange={
                                            this.handleInputChange
                                        }
                                        placeholder="Votre mot de passe"
                                        autoComplete="current-password"
                                        required
                                        style={{
                                            ...styles.input,
                                            paddingRight: 48
                                        }}
                                    />

                                    <button
                                        type="button"
                                        onClick={
                                            this.togglePassword
                                        }
                                        aria-label={
                                            showPassword
                                                ? "Masquer le mot de passe"
                                                : "Afficher le mot de passe"
                                        }
                                        style={
                                            styles.eyeButton
                                        }
                                    >
                                        <i
                                            className={`fa fa-${
                                                showPassword
                                                    ? "eye-slash"
                                                    : "eye"
                                            }`}
                                        />
                                    </button>
                                </div>
                            </FormGroup>

                            {/* OFFLINE USERS */}
                            {!isOnline &&
                                offlineEmails.length >
                                0 && (
                                    <div
                                        style={
                                            styles.offlineUsers
                                        }
                                    >
                                        <div
                                            style={
                                                styles.offlineTitle
                                            }
                                        >
                                            <i className="fa fa-users" />
                                            <span>
                                                Utilisateurs disponibles
                                            </span>
                                        </div>

                                        <div>
                                            {offlineEmails
                                                .slice(
                                                    0,
                                                    3
                                                )
                                                .map(
                                                    (
                                                        userEmail,
                                                        index
                                                    ) => (
                                                        <button
                                                            key={
                                                                index
                                                            }
                                                            type="button"
                                                            onClick={() =>
                                                                this.setState(
                                                                    {
                                                                        email: userEmail
                                                                    }
                                                                )
                                                            }
                                                            style={
                                                                styles.emailChip
                                                            }
                                                        >
                                                            {
                                                                userEmail
                                                            }
                                                        </button>
                                                    )
                                                )}
                                        </div>
                                    </div>
                                )}

                            {/* FORGOT / REGISTER */}
                            {isOnline && (
                                <div
                                    style={
                                        styles.linksRow
                                    }
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            this.setState(
                                                {
                                                    showResetModal:
                                                        true
                                                }
                                            )
                                        }
                                        style={
                                            styles.linkButton
                                        }
                                    >
                                        Mot de passe oublié ?
                                    </button>

                                    <Link
                                        to="/registration"
                                        style={
                                            styles.registerLink
                                        }
                                    >
                                        Créer un compte
                                    </Link>
                                </div>
                            )}

                            {/* BUTTON */}
                            <Button
                                bsStyle={
                                    isOnline
                                        ? "info"
                                        : "warning"
                                }
                                block
                                type="submit"
                                disabled={loading}
                                className="linfomed-login-button"
                                style={
                                    styles.loginButton
                                }
                            >
                                {loading && (
                                    <i
                                        className="fa fa-spinner fa-spin"
                                        style={{
                                            marginRight: 10
                                        }}
                                    />
                                )}

                                <i
                                    className={`fa fa-${
                                        isOnline
                                            ? "sign-in"
                                            : "cloud-download"
                                    }`}
                                    style={{
                                        marginRight: 8
                                    }}
                                />

                                {isOnline
                                    ? "Se connecter"
                                    : "Connexion hors ligne"}
                            </Button>

                            {/* SWITCH OFFLINE */}
                            {isOnline && (
                                <button
                                    type="button"
                                    onClick={
                                        this.handleSwitchToOffline
                                    }
                                    style={
                                        styles.offlineButton
                                    }
                                >
                                    <i className="fa fa-wifi" />
                                    Problème de connexion ?
                                    <strong>
                                        {" "}
                                        Utiliser le mode hors ligne
                                    </strong>
                                </button>
                            )}

                            {/* RETRY */}
                            {!isOnline && (
                                <button
                                    type="button"
                                    onClick={
                                        this.handleRetryConnection
                                    }
                                    disabled={
                                        this.state
                                            .checkingConnectivity
                                    }
                                    style={
                                        styles.retryButton
                                    }
                                >
                                    <i className="fa fa-refresh" />
                                    {" "}
                                    Réessayer la connexion
                                </button>
                            )}
                        </form>

                        {/* PRIVACY */}
                        <div
                            style={
                                styles.privacyContainer
                            }
                        >
                            <button
                                type="button"
                                onClick={() =>
                                    this.setState({
                                        showPrivacyModal:
                                            true
                                    })
                                }
                                style={
                                    styles.privacyButton
                                }
                            >
                                <i className="fa fa-shield" />
                                Politique de confidentialité
                            </button>
                        </div>

                        <div style={styles.footer}>
                            © {new Date().getFullYear()} LINFOMED
                        </div>
                    </div>
                </div>
            </div>
                    </Col>
                </Row>
            </Grid>
        );
    }

    /* =========================================================
       MAIN RENDER
    ========================================================= */

    render() {
        const {
            checkingConnectivity,
            showSubscriptionModal,
            subscriptionStatus,
            payLink
        } = this.state;

        if (checkingConnectivity) {
            return this.renderConnectivityCheck();
        }

        return (
            <>
                <style>
                    {`
                        .linfomed-login-input:focus {
                            border-color: #1769aa !important;
                            box-shadow: 0 0 0 3px rgba(23, 105, 170, 0.10) !important;
                        }

                        .linfomed-login-button:hover {
                            transform: translateY(-1px);
                        }

                        .linfomed-login-button {
                            transition: all 0.2s ease;
                        }

                        .linfomed-login-button.btn-info,
                        .linfomed-login-button.btn-info:hover,
                        .linfomed-login-button.btn-info:focus,
                        .linfomed-login-button.btn-info:active {
                            display: block !important;
                            visibility: visible !important;
                            background-color: var(--app-primary-color) !important;
                            border-color: var(--app-primary-color) !important;
                            color: #fff !important;
                        }

                        @media (max-width: 768px) {
                            .login-image-panel {
                                display: none !important;
                            }
                        }
                    `}
                </style>

                <div style={styles.page}>
                    {this.renderLoginForm()}

                    {/* RESET PASSWORD */}
                    <Modal
                        show={
                            this.state
                                .showResetModal
                        }
                        onHide={() =>
                            this.setState({
                                showResetModal:
                                    false
                            })
                        }
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>
                                Réinitialiser le mot de passe
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            <FormGroup>
                                <ControlLabel>
                                    Adresse email
                                </ControlLabel>

                                <FormControl
                                    type="email"
                                    name="resetEmail"
                                    value={
                                        this.state
                                            .resetEmail
                                    }
                                    onChange={
                                        this.handleInputChange
                                    }
                                    placeholder="Entrez votre adresse email"
                                />
                            </FormGroup>
                        </Modal.Body>

                        <Modal.Footer>
                            <Button
                                onClick={() =>
                                    this.setState({
                                        showResetModal:
                                            false
                                    })
                                }
                            >
                                Annuler
                            </Button>

                            <Button
                                bsStyle="primary"
                                onClick={
                                    this.handleResetPassword
                                }
                            >
                                <i
                                    className="fa fa-paper-plane"
                                    style={{
                                        marginRight: 6
                                    }}
                                />
                                Envoyer
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* PRIVACY */}
                    <Modal
                        show={
                            this.state
                                .showPrivacyModal
                        }
                        onHide={() =>
                            this.setState({
                                showPrivacyModal:
                                    false
                            })
                        }
                        bsSize="lg"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <i
                                    className="fa fa-shield"
                                    style={{
                                        marginRight: 10,
                                        color: "#1769aa"
                                    }}
                                />
                                Politique de confidentialité
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body
                            style={{
                                maxHeight: 500,
                                overflowY: "auto"
                            }}
                        >
                            <div
                                style={{
                                    padding: "10px"
                                }}
                            >
                                <p
                                    style={{
                                        lineHeight: 1.7
                                    }}
                                >
                                    LINFOMED veille au
                                    respect de la
                                    confidentialité sur
                                    Internet et est tenu
                                    par la loi sur la
                                    protection des données
                                    à caractère personnel
                                    en vigueur dans notre
                                    pays.
                                </p>

                                <h4>
                                    <i className="fa fa-user" />
                                    {" "}Utilisation ordinaire
                                </h4>

                                <p
                                    style={{
                                        lineHeight: 1.7
                                    }}
                                >
                                    Les informations
                                    collectées à
                                    l'inscription ou à
                                    l'abonnement permettent
                                    de créer un compte pour
                                    l'utilisateur et de lui
                                    fournir les accès.
                                </p>

                                <h4>
                                    <i className="fa fa-edit" />
                                    {" "}Retrait ou modification
                                </h4>

                                <p
                                    style={{
                                        lineHeight: 1.7
                                    }}
                                >
                                    Le retrait ou la
                                    modification des
                                    informations personnelles
                                    est possible en revenant
                                    sur le formulaire
                                    d'inscription ou en
                                    prenant contact avec le
                                    personnel de LINFOMED.
                                </p>

                                <h4>
                                    <i className="fa fa-lock" />
                                    {" "}Sécurité
                                </h4>

                                <p
                                    style={{
                                        lineHeight: 1.7
                                    }}
                                >
                                    Les données personnelles
                                    confiées ici ne seront en
                                    aucun cas vendues ou
                                    transmises à des tiers.
                                    Toutes les informations
                                    confiées par les usagers
                                    sont conservées avec le
                                    maximum de sécurité.
                                </p>

                                <h4>
                                    <i className="fa fa-sync" />
                                    {" "}Modifications
                                </h4>

                                <p
                                    style={{
                                        lineHeight: 1.7
                                    }}
                                >
                                    Toute modification des
                                    présentes règles sera
                                    disponible sur le lien de
                                    la politique de
                                    confidentialité de
                                    l'application.
                                </p>

                                <div
                                    style={{
                                        background:
                                            "#f8f9fa",
                                        padding: 20,
                                        borderRadius: 10
                                    }}
                                >
                                    <h4>
                                        <i className="fa fa-envelope" />
                                        {" "}Contact
                                    </h4>

                                    <p
                                        style={{
                                            lineHeight: 1.8
                                        }}
                                    >
                                        Pharmacie GEMME,
                                        Tampouy Secteur 16,
                                        Ouagadougou
                                        <br />
                                        Tél : (+226) 56 62 44
                                        54 / (+226) 55 12 75
                                        26
                                        <br />
                                        WhatsApp : (+226) 65
                                        06 62 43
                                        <br />
                                        E-mail :
                                        angelinezoungranabe@gmail.com
                                        {" / "}
                                        tlbienvenue3@yahoo.fr
                                    </p>
                                </div>

                                <h4>
                                    <i className="fa fa-copyright" />
                                    {" "}Droits d'auteur
                                </h4>

                                <p
                                    style={{
                                        lineHeight: 1.7
                                    }}
                                >
                                    © Droits d'auteur –
                                    LINFOMED. Tous droits
                                    réservés.
                                </p>

                                <h4
                                    style={{
                                        color: "#e74c3c"
                                    }}
                                >
                                    <i className="fa fa-exclamation-triangle" />
                                    {" "}Avis de
                                    non-responsabilité
                                </h4>

                                <p
                                    style={{
                                        lineHeight: 1.7
                                    }}
                                >
                                    Les informations
                                    contenues dans cet outil
                                    ne doivent PAS être
                                    utilisées comme substitut
                                    aux conseils d'un
                                    professionnel de santé
                                    dûment qualifié,
                                    expérimenté et agréé.
                                </p>

                                <div
                                    style={{
                                        background:
                                            "#fff3cd",
                                        borderLeft:
                                            "4px solid #ffc107",
                                        padding:
                                            "12px 15px",
                                        borderRadius: 5
                                    }}
                                >
                                    <strong>
                                        Nota Bene :
                                    </strong>{" "}
                                    Bien que nous essayions de
                                    fournir des informations
                                    exactes et à jour, aucune
                                    garantie n'est donnée à
                                    cet effet. Veuillez
                                    vérifier toute
                                    information ou demander
                                    un avis spécialisé.
                                </div>
                            </div>
                        </Modal.Body>

                        <Modal.Footer>
                            <Button
                                bsStyle="primary"
                                onClick={() =>
                                    this.setState({
                                        showPrivacyModal:
                                            false
                                    })
                                }
                            >
                                <i className="fa fa-check" />
                                {" "}J'ai compris
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* SUBSCRIPTION */}
                    <SubscriptionModal
                        show={
                            showSubscriptionModal
                        }
                        onClose={
                            this.handleSubscriptionModalClose
                        }
                        subscriptionStatus={
                            subscriptionStatus
                        }
                        payLink={payLink}
                    />
                </div>
            </>
        );
    }
}

/* =============================================================
   STYLES
============================================================= */

const styles = {
    page: {
        minHeight: "100vh",
        width: "100%",
        background:
            "linear-gradient(135deg, #eef4f8 0%, #f8fafc 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "30px 20px",
        boxSizing: "border-box"
    },

    loginGrid: {
        width: "100%",
        maxWidth: "1080px"
    },

    loginContainer: {
        width: "100%",
        maxWidth: "1050px",
        minHeight: "650px",
        background: "#ffffff",
        borderRadius: "22px",
        overflow: "hidden",
        display: "flex",
        boxShadow:
            "0 25px 70px rgba(20, 50, 70, 0.15)"
    },

    imagePanel: {
        width: "46%",
        minHeight: "650px",
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative"
    },

    imageOverlay: {
        position: "absolute",
        inset: 0,
        padding: "45px",
        background:
            "linear-gradient(160deg, rgba(7,45,73,0.88), rgba(23,105,170,0.72), rgba(0,0,0,0.40))",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between"
    },

    logoCircle: {
        width: "65px",
        height: "65px",
        borderRadius: "18px",
        background: "rgba(255,255,255,0.18)",
        border:
            "1px solid rgba(255,255,255,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "29px",
        backdropFilter: "blur(5px)"
    },

    imageTitle: {
        fontSize: "44px",
        fontWeight: "800",
        letterSpacing: "1px",
        marginBottom: "12px"
    },

    imageSubtitle: {
        fontSize: "17px",
        lineHeight: "1.7",
        maxWidth: "390px",
        color: "rgba(255,255,255,0.9)"
    },

    imageBottom: {
        display: "flex",
        flexDirection: "column",
        gap: "14px"
    },

    formPanel: {
        flex: 1,
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },

    formInner: {
        width: "100%",
        maxWidth: "450px",
        padding: "50px 55px"
    },

    formHeader: {
        marginBottom: "25px"
    },

    statusBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        borderRadius: "30px",
        padding: "7px 13px",
        fontSize: "12px",
        fontWeight: "600",
        marginBottom: "17px"
    },

    statusDot: {
        width: "7px",
        height: "7px",
        borderRadius: "50%"
    },

    formTitle: {
        margin: "0 0 8px",
        fontSize: "31px",
        fontWeight: "750",
        color: "#172b4d"
    },

    formSubtitle: {
        margin: 0,
        color: "#8492a6",
        fontSize: "14px"
    },

    modeBox: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "13px 15px",
        borderRadius: "11px",
        marginBottom: "24px",
        fontSize: "12px",
        color: "#5b6573"
    },

    modeIcon: {
        width: "35px",
        height: "35px",
        borderRadius: "9px",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "15px"
    },

    label: {
        display: "block",
        textAlign: "left",
        color: "#344054",
        fontWeight: "600",
        fontSize: "13px",
        marginBottom: "8px"
    },

    inputWrapper: {
        position: "relative",
        width: "100%"
    },

    inputIcon: {
        position: "absolute",
        left: "15px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 5,
        color: "#98a2b3",
        fontSize: "15px"
    },

    input: {
        height: "50px",
        width: "100%",
        borderRadius: "10px",
        border: "1px solid #d0d5dd",
        paddingLeft: "44px",
        fontSize: "14px",
        boxShadow: "none"
    },

    eyeButton: {
        position: "absolute",
        right: "8px",
        top: "50%",
        transform: "translateY(-50%)",
        width: "38px",
        height: "38px",
        border: "none",
        background: "transparent",
        color: "#667085",
        cursor: "pointer",
        fontSize: "15px",
        zIndex: 10
    },

    linksRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "5px",
        marginBottom: "25px",
        gap: "10px"
    },

    linkButton: {
        background: "none",
        border: "none",
        padding: 0,
        color: "#1769aa",
        cursor: "pointer",
        fontSize: "12px"
    },

    registerLink: {
        color: "#198754",
        fontWeight: "600",
        fontSize: "12px",
        textDecoration: "none"
    },

    loginButton: {
        width: "100%",
        height: "50px",
        borderRadius: "10px",
        fontSize: "15px",
        fontWeight: "700",
        backgroundColor: "var(--app-primary-color)",
        color: "#fff",
        border: "none",
        boxShadow:
            "0 7px 20px rgba(23,105,170,0.18)"
    },

    offlineButton: {
        width: "100%",
        marginTop: "17px",
        border: "none",
        background: "transparent",
        color: "#b77900",
        fontSize: "11px",
        cursor: "pointer"
    },

    retryButton: {
        width: "100%",
        marginTop: "15px",
        padding: "10px",
        borderRadius: "8px",
        border: "1px solid var(--app-primary-color)",
        background: "transparent",
        color: "var(--app-primary-color)",
        cursor: "pointer",
        fontSize: "12px"
    },

    offlineUsers: {
        padding: "12px",
        borderRadius: "10px",
        background: "#f8fafc",
        border: "1px solid #eaecf0",
        marginBottom: "20px"
    },

    offlineTitle: {
        display: "flex",
        gap: "7px",
        alignItems: "center",
        color: "#667085",
        fontSize: "11px",
        fontWeight: "600",
        marginBottom: "8px"
    },

    emailChip: {
        background: "#fff",
        border: "1px solid #d0d5dd",
        borderRadius: "20px",
        padding: "5px 9px",
        margin: "2px",
        fontSize: "10px",
        cursor: "pointer",
        color: "#475467"
    },

    privacyContainer: {
        textAlign: "center",
        borderTop: "1px solid #eaecf0",
        marginTop: "25px",
        paddingTop: "17px"
    },

    privacyButton: {
        background: "none",
        border: "none",
        color: "#98a2b3",
        fontSize: "11px",
        cursor: "pointer"
    },

    footer: {
        textAlign: "center",
        color: "#c0c5cc",
        fontSize: "10px",
        marginTop: "15px"
    },

    successContainer: {
        width: "100%",
        maxWidth: "1050px",
        minHeight: "600px",
        background: "#fff",
        borderRadius: "22px",
        boxShadow:
            "0 25px 70px rgba(20, 50, 70, 0.15)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center"
    },

    successIcon: {
        width: "80px",
        height: "80px",
        borderRadius: "50%",
        background: "#e8f7ee",
        color: "#198754",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "35px",
        marginBottom: "20px"
    },

    loadingCard: {
        width: "100%",
        maxWidth: "700px",
        minHeight: "450px",
        borderRadius: "22px",
        overflow: "hidden",
        backgroundSize: "cover",
        backgroundPosition: "center"
    },

    loadingOverlay: {
        minHeight: "450px",
        background:
            "rgba(0,30,55,0.80)",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center"
    },

    spinnerCircle: {
        fontSize: "38px",
        marginBottom: "20px"
    },

    mobileLogo: {
        display: "none"
    },

    mobileLogoIcon: {
        width: "45px",
        height: "45px",
        borderRadius: "12px",
        background: "#1769aa",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: "10px"
    }
};

/* =============================================================
   PROPTYPES
============================================================= */

Login.propTypes = {
    fetchUser: PropTypes.func.isRequired,
    signIn: PropTypes.func.isRequired,
    resetPassword: PropTypes.func.isRequired,
    setMessage: PropTypes.func.isRequired,
    authenticateOffline:
    PropTypes.func.isRequired,
    syncFirestoreUsersToPouch:
    PropTypes.func.isRequired,
    triggerOfflineSync:
    PropTypes.func.isRequired,

    user: PropTypes.object,
    profile: PropTypes.object,
    message: PropTypes.object,

    loading: PropTypes.bool,
    authenticating: PropTypes.bool,
    authDone: PropTypes.bool,

    offlineUser: PropTypes.object,
    offlineAuthDone: PropTypes.bool,

    history: PropTypes.object.isRequired
};

/* =============================================================
   REDUX
============================================================= */

const mapStateToProps = state => ({
    user: state.user.user,
    profile: state.user.profile,

    message: state.status.message,
    loading: state.status.loading,
    authenticating:
    state.status.authenticating,

    authDone:
    state.user.authDone,

    offlineUser:
    state.user.offlineUser,

    offlineAuthDone:
    state.user.offlineAuthDone
});

const triggerOfflineSync =
    () => dispatch =>
        runBackgroundOfflineSync(dispatch);

const mapDispatchToProps = {
    fetchUser,
    signIn,
    resetPassword,
    setMessage,
    authenticateOffline,
    syncFirestoreUsersToPouch,
    triggerOfflineSync
};

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(Login)
);